import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
    FaGlobe,
    FaCheckCircle,
    FaTimesCircle,
    FaArrowRight,
} from "react-icons/fa";
import BasicButton from "../../Buttons/BasicButton";
import { CreateCustomDomain } from "../../../Api/apis/customDomainApi";
import { CustomDomainListData } from "../types";
import DomainValidationRuleEngine, {
    DomainRuleStatus,
    DomainServerError,
    HOSTNAME_FORMAT_REGEX,
    isReservedDomain,
    getDomainRuleStatus,
} from "./DomainValidationRuleEngine";

interface CustomDomainAddProps {
    onCancel: () => void;
    onSuccess: (domain: CustomDomainListData) => void;
}

interface CustomDomainAddFormValues {
    hostname: string;
}

// Mirrors backend's validate_hostname exactly (see AddCustomDomainAPIView) so a
// user never has to hit submit to find out the domain is malformed. abortEarly
// (yup's default) means only the first failing rule's message surfaces at a time.
const domainSchema = yup.object({
    hostname: yup
        .string()
        .trim()
        .required("customDomains.ruleRequiredMessage")
        .test(
            "no-protocol",
            "customDomains.ruleProtocolMessage",
            (value) =>
                !!value &&
                !value.toLowerCase().startsWith("http://") &&
                !value.toLowerCase().startsWith("https://"),
        )
        .test(
            "no-slash-or-colon",
            "customDomains.ruleSlashColonMessage",
            (value) => !!value && !value.includes("/") && !value.includes(":"),
        )
        .test(
            "not-reserved",
            "customDomains.rulePlatformDomainMessage",
            (value) => !!value && !isReservedDomain(value),
        )
        .test(
            "no-wildcard",
            "customDomains.ruleWildcardMessage",
            (value) => !!value && !value.includes("*"),
        )
        .test(
            "valid-format",
            "customDomains.ruleFormatMessage",
            (value) => !!value && HOSTNAME_FORMAT_REGEX.test(value),
        ),
});

const CustomDomainAdd: React.FC<CustomDomainAddProps> = ({
    onCancel,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [serverError, setServerError] = useState<DomainServerError | null>(
        null,
    );

    const {
        register,
        handleSubmit,
        watch,
        setError,
        clearErrors,
        formState: { touchedFields, isSubmitted, isSubmitting },
    } = useForm<CustomDomainAddFormValues>({
        resolver: yupResolver(domainSchema),
        mode: "onChange",
        defaultValues: { hostname: "" },
    });

    const hostname = watch("hostname") || "";
    const touched = !!touchedFields.hostname || isSubmitted;

    // Clear a stale server-side rejection as soon as the user edits the value again.
    useEffect(() => {
        if (serverError) {
            setServerError(null);
            clearErrors("hostname");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostname]);

    const isValid =
        getDomainRuleStatus(hostname, { touched: true }) ===
        DomainRuleStatus.VALID;

    const onSubmit = async (formValues: CustomDomainAddFormValues) => {
        const normalizedHostname = formValues.hostname.trim().toLowerCase();
        try {
            const response = await CreateCustomDomain({
                hostname: normalizedHostname,
            });
            toast.success(response.data.message);
            onSuccess(response.data.data);
        } catch (error: any) {
            const httpStatus = error?.response?.status;
            const responseData = error?.response?.data;

            // Not a vendor / no store - account-level issue, not a hostname problem.
            if (
                httpStatus === 401 ||
                responseData?.error === "Unauthorized" ||
                responseData?.error === "Store Not Found"
            ) {
                toast.error(
                    responseData?.message ||
                        t("customDomains.ruleServerErrorFallbackMessage"),
                );
                return;
            }

            // serializer.errors shape: { hostname: ["..."] } when validation is somehow
            // bypassed client-side; otherwise fall back to the top-level message
            // (duplicate domain, per-store limit reached, pending-by-other-vendor, etc).
            const fieldMessage = responseData?.error?.hostname?.[0];
            const message =
                (typeof fieldMessage === "string"
                    ? fieldMessage
                    : responseData?.message) ||
                t("customDomains.ruleServerErrorFallbackMessage");

            toast.error(message);
            setServerError({ message });
            setError("hostname", { type: "server", message });
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <h3 className="fs-4 fw-bold text-dark mb-0">
                    {t("customDomains.enterYourDomain")}
                </h3>
            </div>
            <p className="text-muted small mb-0">
                {t("customDomains.enterDomainDescription")}
            </p>

            <form
                onSubmit={handleSubmit(onSubmit)}
                className="d-flex flex-column gap-3"
            >
                <div>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                        <label
                            htmlFor="hostname"
                            className="form-label fw-semibold mb-0"
                        >
                            {t("customDomains.domainNameLabel")}{" "}
                            <span className="text-primary">&bull;</span>
                        </label>
                        <span className="text-muted small">
                            {t("customDomains.domainNameHint")}
                        </span>
                    </div>
                    <div className="input-group">
                        <span className="input-group-text bg-white">
                            <FaGlobe className="text-muted" />
                        </span>
                        <input
                            id="hostname"
                            type="text"
                            className="form-control"
                            placeholder={t(
                                "customDomains.domainNamePlaceholder",
                            )}
                            autoComplete="off"
                            {...register("hostname")}
                        />
                        {touched && hostname.trim() && (
                            <span className="input-group-text bg-white">
                                {isValid ? (
                                    <FaCheckCircle className="text-success" />
                                ) : (
                                    <FaTimesCircle className="text-danger" />
                                )}
                            </span>
                        )}
                    </div>
                </div>

                <DomainValidationRuleEngine
                    value={hostname}
                    touched={touched}
                    serverError={serverError}
                />

                <div className="d-flex justify-content-between flex-wrap gap-2 pt-2">
                    <BasicButton
                        type="button"
                        variant="ghost"
                        onClick={onCancel}
                    >
                        {t("customDomains.cancelAndBack")}
                    </BasicButton>
                    <BasicButton
                        type="submit"
                        variant="basic"
                        disabled={isSubmitting}
                    >
                        {t("customDomains.continueToDnsVerification")}
                        <FaArrowRight className="ms-2" />
                    </BasicButton>
                </div>
            </form>
        </div>
    );
};

export default CustomDomainAdd;
